import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import {
  CalendarDots, Factory, Invoice, Receipt, ArrowCircleDown, ArrowCircleUp,
  ChartPieSlice, CheckCircle, Warning, SignOut, Gear, Sun, Moon, List, X, ArrowsDownUp, Bank, CheckSquare,
} from "@phosphor-icons/react";

const LOGO_BLACK = "https://customer-assets-gfyr7b9c.emergentagent.net/job_finance-command-31/artifacts/ckdby44t_siyah.png";
const LOGO_WHITE = "https://customer-assets-gfyr7b9c.emergentagent.net/job_finance-command-31/artifacts/dq56m5hm_beyaz.png";

const navGroups = [
  { label: "Operasyon", items: [
    { to: "/etkinlikler", label: "Etkinlikler", icon: CalendarDots },
    { to: "/uretimler", label: "Üretimler", icon: Factory },
    { to: "/raporlar", label: "Raporlar", icon: CheckCircle },
  ] },
  { label: "Finans", items: [
    { to: "/satis-faturalari", label: "Satış Faturaları", icon: Invoice },
    { to: "/alis-faturalari", label: "Alış Faturaları", icon: Receipt },
    { to: "/gelen-odemeler", label: "Gelen Ödemeler", icon: ArrowCircleDown },
    { to: "/giden-odemeler", label: "Giden Ödemeler", icon: ArrowCircleUp },
    { to: "/nakit-akisi", label: "Nakit Akışı", icon: ArrowsDownUp },
    { to: "/bankacilik", label: "Bankacılık", icon: Bank },
    { to: "/gerceklesen", label: "Gerçekleşen", icon: ChartPieSlice },
  ] },
  { label: "Sistem", items: [
    { to: "/uyarilar", label: "Uyarılar", icon: Warning },
    { to: "/ayarlar", label: "Ayarlar", icon: Gear, testid: "nav-settings" },
  ] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const openTasks = async () => {
    const taskWindow = window.open("/api/auth/todo-sso", "flum-tasks-sso");
    if (!taskWindow) window.alert("Tarayıcınız yeni sekme açılmasını engelledi. Lütfen bu site için açılır pencereye izin verin.");
  };

  return (
    <div className="min-h-screen lg:flex lg:h-screen lg:overflow-hidden bg-[#F8F9FA]" data-testid="app-shell">
      <header className="sticky top-0 z-40 h-16 px-4 border-b border-[#D4D4D4] bg-white flex items-center gap-3 lg:hidden">
        <button
          type="button"
          onClick={() => setMobileSidebarOpen(true)}
          className="p-2 -ml-2 rounded-sm hover:bg-neutral-100"
          aria-label="Menüyü aç"
          data-testid="mobile-menu-open"
        >
          <List size={24} weight="bold" />
        </button>
        <NavLink to="/main" aria-label="Ana sayfaya dön"><img src={theme === "dark" ? LOGO_WHITE : LOGO_BLACK} alt="FLUM" className="h-5 w-auto" /></NavLink>
      </header>

      {mobileSidebarOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
          aria-label="Menüyü kapat"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 border-r border-[#D4D4D4] bg-white flex flex-col overflow-hidden transition-[width,transform] duration-200 lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 ${
          mobileSidebarOpen ? "translate-x-0" : "-translate-x-full"
        } w-72 ${sidebarCollapsed ? "lg:w-20" : "lg:w-64"}`}
        data-testid="sidebar"
      >
        {sidebarCollapsed ? (
          <>
            <div className="px-5 py-5 border-b border-[#E5E5E5] flex items-start justify-between gap-4 lg:hidden">
              <NavLink to="/main" onClick={() => setMobileSidebarOpen(false)}>
                <img src={theme === "dark" ? LOGO_WHITE : LOGO_BLACK} alt="FLUM" className="h-6 w-auto" />
                <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500 mt-3">Flum World</div>
              </NavLink>
              <button
                type="button"
                onClick={() => setMobileSidebarOpen(false)}
                className="p-2 -mt-2 -mr-2 rounded-sm hover:bg-neutral-100"
                aria-label="Menüyü kapat"
              >
                <X size={21} weight="bold" />
              </button>
            </div>
            <div className="hidden lg:flex h-[89px] items-center justify-center border-b border-[#E5E5E5]">
              <button
                type="button"
                onClick={() => setSidebarCollapsed(false)}
                className="p-2 rounded-sm hover:bg-neutral-100"
                aria-label="Menüyü aç"
                title="Menüyü aç"
                data-testid="desktop-menu-open"
              >
                <List size={23} weight="bold" />
              </button>
            </div>
          </>
        ) : (
          <div className="px-5 py-5 border-b border-[#E5E5E5] flex items-start justify-between gap-4">
            <NavLink to="/main" className="min-w-0" aria-label="Flum World ana sayfa">
              <img
                src={theme === "dark" ? LOGO_WHITE : LOGO_BLACK}
                alt="FLUM"
                className="h-6 w-auto"
                data-testid="brand-logo"
              />
              <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500 mt-3">Flum World</div>
            </NavLink>
            <button
              type="button"
              onClick={() => setMobileSidebarOpen(false)}
              className="p-2 -mt-2 -mr-2 rounded-sm hover:bg-neutral-100 lg:hidden"
              aria-label="Menüyü kapat"
              data-testid="menu-close"
            >
              <X size={21} weight="bold" />
            </button>
            <button
              type="button"
              onClick={() => setSidebarCollapsed(true)}
              className="hidden lg:block p-2 -mt-2 -mr-2 rounded-sm hover:bg-neutral-100"
              aria-label="Menüyü kapat"
              title="Menüyü kapat"
              data-testid="desktop-menu-close"
            >
              <X size={21} weight="bold" />
            </button>
          </div>
        )}
        <nav className={`flex-1 py-4 space-y-4 overflow-y-auto ${sidebarCollapsed ? "lg:px-3" : "px-3"}`}>
          {navGroups.map((group) => <div key={group.label}>
            <div className={`label-mini mb-1 px-3 ${sidebarCollapsed ? "lg:hidden" : ""}`}>{group.label}</div>
            <div className="space-y-0.5">{group.items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                data-testid={it.testid}
                onClick={() => setMobileSidebarOpen(false)}
                aria-label={it.label}
                title={sidebarCollapsed ? it.label : undefined}
                className={({ isActive }) => `nav-item ${sidebarCollapsed ? "lg:justify-center lg:px-0" : ""} ${isActive ? "active" : ""}`}
              >
                <it.icon size={18} weight="duotone" className="shrink-0" />
                <span className={`text-sm ${sidebarCollapsed ? "lg:hidden" : ""}`}>{it.label}</span>
              </NavLink>
            ))}</div>
          </div>)}
        </nav>
        <div className="px-3 pb-3">
          <button
            type="button"
            onClick={openTasks}
            title={sidebarCollapsed ? "Flum Tasks'a geç" : undefined}
            className={`nav-item w-full ${sidebarCollapsed ? "lg:justify-center lg:px-0" : ""}`}
          >
            <CheckSquare size={18} weight="duotone" className="shrink-0" />
            <span className={`text-sm ${sidebarCollapsed ? "lg:hidden" : ""}`}>Flum Tasks'a Geç</span>
          </button>
        </div>
        <div className="border-t border-[#E5E5E5] p-4">
          <div className={`flex items-center gap-3 ${sidebarCollapsed ? "lg:flex-col" : "justify-between"}`}>
            <div className={`min-w-0 ${sidebarCollapsed ? "lg:hidden" : ""}`}>
              <div className="text-sm font-semibold truncate" data-testid="current-user-name">{user?.name || "Kullanıcı"}</div>
              <div className="text-xs text-neutral-500 truncate">{user?.email}</div>
            </div>
            <div className={`flex items-center gap-1 ${sidebarCollapsed ? "lg:flex-col" : ""}`}>
              <button
                onClick={toggle}
                data-testid="theme-toggle-button"
                className="p-2 rounded-sm hover:bg-neutral-100 transition-colors"
                aria-label={theme === "dark" ? "Aydınlık moda geç" : "Karanlık moda geç"}
                title={theme === "dark" ? "Aydınlık mod" : "Karanlık mod"}
              >
                {theme === "dark" ? <Sun size={18} weight="bold" /> : <Moon size={18} weight="bold" />}
              </button>
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className="p-2 rounded-sm hover:bg-neutral-100 transition-colors"
                aria-label="Çıkış Yap"
              >
                <SignOut size={18} weight="bold" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      <main className="min-w-0 flex-1 overflow-x-hidden lg:h-screen lg:overflow-y-auto">
        <div className="max-w-[1400px] mx-auto px-4 py-5 sm:px-6 lg:px-8 lg:py-8 fade-in">
          {children}
        </div>
      </main>
    </div>
  );
}
